import React from "react";
import { Linkedin, Github, Mail, ArrowUpRight } from "lucide-react";
import { personalInfo, RESUME_URL } from "../data/mock";
import axios from "axios";

const BACKEND_URL = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trackResumeDownload = () => {
  axios.post(`${API}/resume/track`).catch(() => {});
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { label: "About", href: "#about" },
    { label: "Skills", href: "#skills" },
    { label: "Projects", href: "#projects" },
    { label: "Experience", href: "#experience" },
    { label: "Contact", href: "#contact" },
  ];

  const socialLinks = [
    {
      label: "LinkedIn",
      href: personalInfo.linkedin,
      icon: Linkedin,
    },
    {
      label: "GitHub",
      href: personalInfo.github,
      icon: Github,
    },
    {
      label: "Email",
      href: `mailto:${personalInfo.email}`,
      icon: Mail,
    },
  ];

  const scrollToSection = (href) => {
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <footer className="relative border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-3 gap-12 sm:gap-8 mb-16">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold text-white font-mono tracking-tight">
                {personalInfo.initials}
              </span>
              <span className="text-blue-400 font-mono">.</span>
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed max-w-xs">
              {personalInfo.title} based in {personalInfo.location}.
              Focused on cloud infrastructure, automation, and secure enterprise
              environments.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
              Navigation
            </h4>
            <ul className="space-y-2.5">
              {footerLinks.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-sm text-zinc-400 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
              Connect
            </h4>
            <ul className="space-y-2.5">
              {socialLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200"
                  >
                    <link.icon size={15} />
                    <span>{link.label}</span>
                    <ArrowUpRight
                      size={12}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    />
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={RESUME_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackResumeDownload}
                  className="group inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200"
                >
                  <span>Resume</span>
                  <ArrowUpRight
                    size={12}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-zinc-600">
            &copy; {currentYear} {personalInfo.name}. All rights reserved.
          </p>
          <p className="text-xs text-zinc-700">
            Designed & built with precision
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
